/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Mark, markPasteRule, mergeAttributes } from "@tiptap/core";
import { find, registerCustomProtocol, reset } from "linkifyjs";

import { autolink } from "./autolink.js";
import { clickHandler } from "./click-handler.js";
import { pasteHandler } from "./paste-handler.js";
import { UNICODE_WHITESPACE_REGEX_GLOBAL } from "./whitespace.js";

export const pasteRegex = /https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z]{2,}\b(?:[-a-zA-Z0-9@:%._+~#=?!&/]*)(?:[-a-zA-Z0-9@:%._+~#=?!&/]*)/gi;

export function isAllowedUri (uri, protocols) {
    const allowedProtocols = [
        "http", "https", "ftp", "ftps", "mailto", "tel", "callto", "sms", "cid", "xmpp"
    ];

    if (protocols) {
        protocols.forEach((protocol) => {
            const isStringProtocol = typeof protocol === "string";
            const nextProtocol = isStringProtocol ? protocol : protocol.scheme;

            if (nextProtocol) {
                allowedProtocols.push(nextProtocol);
            }
        });
    }

    return (
        !uri ||
        uri.replace(UNICODE_WHITESPACE_REGEX_GLOBAL, "").match(
            new RegExp(
                // eslint-disable-next-line no-useless-escape
                `^(?:(?:${allowedProtocols.join("|")}):|[^a-z]|[a-z0-9+.\-]+(?:[^a-z+.\-:]|$))`,
                "i",
            ),
        )
    );
}

/**
 * This extension allows you to create links.
 * @see https://www.tiptap.dev/api/marks/link
 */
export const Link = Mark.create({
    name: "link",

    priority: 1000,

    keepOnSplit: false,

    exitable: true,

    onCreate () {
        if (this.options.validate && !this.options.shouldAutoLink) {
            // Copy the validate function to the shouldAutoLink option
            this.options.shouldAutoLink = this.options.validate;
            console.warn("The `validate` option is deprecated. Rename to the `shouldAutoLink` option instead.");
        }
        this.options.protocols.forEach((protocol) => {
            if (typeof protocol === "string") {
                registerCustomProtocol(protocol);
                return;
            }
            registerCustomProtocol(protocol.scheme, protocol.optionalSlashes);
        });
    },

    onDestroy () {
        reset();
    },

    inclusive () {
        return this.options.autolink;
    },

    addOptions () {
        return {
            openOnClick: true,
            enableClickSelection: false,
            linkOnPaste: true,
            autolink: true,
            protocols: [],
            defaultProtocol: "http",
            HTMLAttributes: {
                target: "_blank",
                rel: "noopener noreferrer nofollow",
                class: null,
            },
            isAllowedUri: (url, ctx) => !!isAllowedUri(url, ctx.protocols),
            validate: (url) => !!url,
            shouldAutoLink: (url) => !!url,
        };
    },

    addAttributes () {
        return {
            href: {
                default: null,
                parseHTML (element) {
                    return element.getAttribute("href");
                },
            },
            target: { default: this.options.HTMLAttributes.target },
            rel: { default: this.options.HTMLAttributes.rel },
            class: { default: this.options.HTMLAttributes.class },
        };
    },

    parseHTML () {
        return [
            {
                tag: "a[href]",
                getAttrs: (dom) => {
                    const href = dom.getAttribute("href");

                    // prevent XSS attacks
                    if (
                        !href ||
                        !this.options.isAllowedUri(href, {
                            defaultValidate: (url) => !!isAllowedUri(url, this.options.protocols),
                            protocols: this.options.protocols,
                            defaultProtocol: this.options.defaultProtocol,
                        })
                    ) {
                        return false;
                    }
                    return null;
                },
            },
        ];
    },

    renderHTML ({ HTMLAttributes }) {
        // prevent XSS attacks
        if (
            !this.options.isAllowedUri(HTMLAttributes.href, {
                defaultValidate: (href) => !!isAllowedUri(href, this.options.protocols),
                protocols: this.options.protocols,
                defaultProtocol: this.options.defaultProtocol,
            })
        ) {
            // strip out the href
            return ["a", mergeAttributes(this.options.HTMLAttributes, { ...HTMLAttributes, href: "" }), 0];
        }

        return ["a", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },

    addCommands () {
        return {
            setLink:
                (attributes) =>
                    ({ chain }) => {
                        const href = attributes?.href;

                        if (
                            !this.options.isAllowedUri(href, {
                                defaultValidate: (url) => !!isAllowedUri(url, this.options.protocols),
                                protocols: this.options.protocols,
                                defaultProtocol: this.options.defaultProtocol,
                            })
                        ) {
                            return false;
                        }

                        return chain().setMark(this.name, attributes)
                            .setMeta("preventAutolink", true)
                            .run();
                    },

            toggleLink:
                (attributes) =>
                    ({ chain }) => {
                        const href = attributes?.href;

                        if (
                            href &&
                            !this.options.isAllowedUri(href, {
                                defaultValidate: (url) => !!isAllowedUri(url, this.options.protocols),
                                protocols: this.options.protocols,
                                defaultProtocol: this.options.defaultProtocol,
                            })
                        ) {
                            return false;
                        }

                        return chain()
                            .toggleMark(this.name, attributes, { extendEmptyMarkRange: true })
                            .setMeta("preventAutolink", true)
                            .run();
                    },

            unsetLink:
                () =>
                    ({ chain }) => {
                        return chain().unsetMark(this.name, { extendEmptyMarkRange: true })
                            .setMeta("preventAutolink", true)
                            .run();
                    },
        };
    },

    addPasteRules () {
        return [
            markPasteRule({
                find: (text) => {
                    const foundLinks = [];

                    if (text) {
                        const { protocols, defaultProtocol } = this.options;
                        const links = find(text).filter((item) =>
                            item.isLink &&
                            this.options.isAllowedUri(item.value, {
                                defaultValidate: (href) => !!isAllowedUri(href, protocols),
                                protocols,
                                defaultProtocol,
                            }),
                        );

                        if (links.length) {
                            links.forEach((link) =>
                                foundLinks.push({
                                    text: link.value,
                                    data: {
                                        href: link.href,
                                    },
                                    index: link.start,
                                }),
                            );
                        }
                    }

                    return foundLinks;
                },
                type: this.type,
                getAttributes: (match) => {
                    return {
                        href: match.data?.href,
                    };
                },
            }),
        ];
    },

    addProseMirrorPlugins () {
        const plugins = [];
        const { protocols, defaultProtocol } = this.options;

        if (this.options.autolink) {
            plugins.push(
                autolink({
                    type: this.type,
                    defaultProtocol: this.options.defaultProtocol,
                    validate: (url) =>
                        this.options.isAllowedUri(url, {
                            defaultValidate: (href) => !!isAllowedUri(href, protocols),
                            protocols,
                            defaultProtocol,
                        }),
                    shouldAutoLink: this.options.shouldAutoLink,
                }),
            );
        }

        if (this.options.openOnClick === true) {
            plugins.push(
                clickHandler({
                    type: this.type,
                    editor: this.editor,
                    enableClickSelection: this.options.enableClickSelection,
                }),
            );
        }

        if (this.options.linkOnPaste) {
            plugins.push(
                pasteHandler({
                    editor: this.editor,
                    defaultProtocol: this.options.defaultProtocol,
                    type: this.type,
                }),
            );
        }

        return plugins;
    },
});
